import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { usePublicSurvey, useSubmitSurvey } from "@/hooks/useCustomerSuccess"
import { Loader2, CheckCircle, AlertTriangle, Heart } from "lucide-react"

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(" ")
}

const SCORE_LABELS: Record<number, string> = {
  0: "Muito improvavel",
  1: "Muito improvavel",
  2: "Improvavel",
  3: "Improvavel",
  4: "Pouco provavel",
  5: "Neutro",
  6: "Razoavel",
  7: "Provavel",
  8: "Muito provavel",
  9: "Extremamente provavel",
  10: "Extremamente provavel",
}

function scoreButtonColor(score: number, selected: number | null): string {
  const isSelected = score === selected
  if (score <= 6) {
    return isSelected
      ? "bg-red-500 text-white border-red-500 shadow-lg shadow-red-500/30 scale-110"
      : "bg-red-500/10 text-red-500 border-red-500/30 hover:bg-red-500/20"
  }
  if (score <= 8) {
    return isSelected
      ? "bg-amber-500 text-white border-amber-500 shadow-lg shadow-amber-500/30 scale-110"
      : "bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500/20"
  }
  return isSelected
    ? "bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/30 scale-110"
    : "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/20"
}

export default function NpsRespondPage() {
  const { token } = useParams<{ token: string }>()
  const { data: survey, isLoading, isError } = usePublicSurvey(token || null)
  const submitSurvey = useSubmitSurvey()
  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  const [comment, setComment] = useState("")
  const [improvement, setImprovement] = useState("")
  const [submitted, setSubmitted] = useState(false)

  // Force dark mode on this page
  useEffect(() => {
    document.documentElement.classList.add("dark")
    return () => {
      // restore on unmount
      const stored = localStorage.getItem("nai-theme")
      if (stored !== "dark") document.documentElement.classList.remove("dark")
    }
  }, [])

  const handleSubmit = async () => {
    if (selectedScore === null || !token) return
    try {
      await submitSurvey.mutateAsync({
        token,
        data: {
          score: selectedScore,
          comment: comment.trim() || undefined,
          improvement: improvement.trim() || undefined,
        },
      })
      setSubmitted(true)
    } catch {
      // error handled by mutation
    }
  }

  // Already responded
  if (survey && survey.status === "responded") {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto" />
          <h1 className="text-2xl font-bold">Pesquisa ja respondida</h1>
          <p className="text-gray-400">
            Voce ja enviou sua avaliacao. Agradecemos pelo feedback!
          </p>
        </div>
      </div>
    )
  }

  // Thank you screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6 animate-fade-in">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">Obrigado!</h1>
          <p className="text-gray-400 text-lg">
            Sua avaliacao e muito importante para nos.
            {selectedScore !== null && selectedScore <= 6 && (
              <span className="block mt-2 text-sm">
                Vamos analisar seu feedback e entrar em contato em breve.
              </span>
            )}
          </p>
          <div className="pt-4 border-t border-gray-800">
            <img
              src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png"
              alt="NAI"
              className="h-8 mx-auto opacity-60"
            />
          </div>
        </div>
      </div>
    )
  }

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
      </div>
    )
  }

  // Error / not found
  if (isError || !survey) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto" />
          <h1 className="text-2xl font-bold">Pesquisa nao encontrada</h1>
          <p className="text-gray-400">
            Este link pode ter expirado ou ser invalido.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#00284a] to-[#003d6b] py-6 px-4">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <img
            src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png"
            alt="NAI"
            className="h-10"
          />
          <span className="text-xs text-white/60 uppercase tracking-wider">Pesquisa de Satisfacao</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-4 py-8 space-y-8">
        {/* Greeting */}
        <div className="text-center space-y-2">
          {survey.contact_name && (
            <p className="text-gray-400">
              Ola, <span className="text-white font-medium">{survey.contact_name}</span>
            </p>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
            Em uma escala de 0 a 10, o quanto voce recomendaria a <span className="text-[#00d4aa]">NAI</span> para um amigo ou colega?
          </h1>
          {survey.company_name && (
            <p className="text-sm text-gray-500">Avaliacao referente a: {survey.company_name}</p>
          )}
        </div>

        {/* Score Buttons */}
        <div className="space-y-3">
          <div className="grid grid-cols-6 sm:grid-cols-11 gap-2">
            {Array.from({ length: 11 }, (_, i) => (
              <button
                key={i}
                onClick={() => setSelectedScore(i)}
                className={cn(
                  "w-full aspect-square rounded-xl border-2 font-bold text-lg transition-all duration-200",
                  scoreButtonColor(i, selectedScore),
                )}
              >
                {i}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500 px-1">
            <span>Nada provavel</span>
            <span>Muito provavel</span>
          </div>
          {selectedScore !== null && (
            <p className="text-center text-sm text-gray-400 animate-fade-in">
              {SCORE_LABELS[selectedScore]}
            </p>
          )}
        </div>

        {/* Comment fields - shown after score selection */}
        {selectedScore !== null && (
          <div className="space-y-4 animate-fade-in">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Heart className="w-4 h-4 inline mr-1 text-[#00d4aa]" />
                Qual o principal motivo da sua nota?
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Conte-nos o que motivou sua avaliacao..."
                rows={3}
                className="w-full rounded-xl border-2 border-gray-700 bg-gray-800/50 text-white placeholder-gray-500 p-4 text-sm focus:border-[#00d4aa] focus:ring-1 focus:ring-[#00d4aa] outline-none transition-colors resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                O que faria aumentar sua nota?
              </label>
              <textarea
                value={improvement}
                onChange={(e) => setImprovement(e.target.value)}
                placeholder="Sugestoes de melhoria..."
                rows={3}
                className="w-full rounded-xl border-2 border-gray-700 bg-gray-800/50 text-white placeholder-gray-500 p-4 text-sm focus:border-[#00d4aa] focus:ring-1 focus:ring-[#00d4aa] outline-none transition-colors resize-none"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitSurvey.isPending}
              className="w-full py-4 rounded-xl font-semibold text-lg bg-gradient-to-r from-[#00d4aa] to-[#00b894] text-[#00284a] hover:shadow-lg hover:shadow-[#00d4aa]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitSurvey.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>Enviar Avaliacao</>
              )}
            </button>

            {submitSurvey.isError && (
              <p className="text-center text-sm text-red-400">
                Erro ao enviar. Tente novamente.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-800 mt-12 py-6 text-center">
        <p className="text-xs text-gray-600">
          Pesquisa confidencial &bull; NAI IT &bull; nai-it.com.br
        </p>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  )
}
